/**
 * Mock Room Types and Rooms
 */

import type { CpRoomType, CpRoom } from '../../../types/curriculum_planning';

const now = Date.now();

export const mockCpRoomTypes: CpRoomType[] = [
  {
    id: 'rt-general',
    name: 'General Classroom',
    code: 'GEN',
    description: 'Standard classroom for general teaching',
    createdAt: now,
  },
  {
    id: 'rt-science',
    name: 'Science Laboratory',
    code: 'SCI',
    description: 'Science lab with equipment for practicals',
    createdAt: now,
  },
  {
    id: 'rt-ict',
    name: 'ICT Suite',
    code: 'ICT',
    description: 'Computer room with workstations',
    createdAt: now,
  },
  {
    id: 'rt-art',
    name: 'Art Studio',
    code: 'ART',
    description: 'Art room with specialist equipment',
    createdAt: now,
  },
  {
    id: 'rt-music',
    name: 'Music Room',
    code: 'MUS',
    description: 'Music room with instruments',
    createdAt: now,
  },
  {
    id: 'rt-dt',
    name: 'DT Workshop',
    code: 'DT',
    description: 'Design & Technology workshop',
    createdAt: now,
  },
  {
    id: 'rt-food',
    name: 'Food Technology',
    code: 'FOOD',
    description: 'Food technology kitchen',
    createdAt: now,
  },
  {
    id: 'rt-drama',
    name: 'Drama Studio',
    code: 'DRAMA',
    description: 'Drama studio/performance space',
    createdAt: now,
  },
  {
    id: 'rt-sports',
    name: 'Sports Hall',
    code: 'SPORTS',
    description: 'Sports hall/gymnasium',
    createdAt: now,
  },
];

export const mockCpRooms: CpRoom[] = [
  // General Classrooms (Block A)
  {
    id: 'room-a101',
    name: 'A101',
    code: 'A101',
    roomTypeId: 'rt-general',
    capacity: 30,
    resources: ['Whiteboard', 'Projector', 'Computer'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-a102',
    name: 'A102',
    code: 'A102',
    roomTypeId: 'rt-general',
    capacity: 30,
    resources: ['Whiteboard', 'Projector', 'Computer'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-a103',
    name: 'A103',
    code: 'A103',
    roomTypeId: 'rt-general',
    capacity: 30,
    resources: ['Whiteboard', 'Projector', 'Computer'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-a104',
    name: 'A104',
    code: 'A104',
    roomTypeId: 'rt-general',
    capacity: 32,
    resources: ['Whiteboard', 'Projector', 'Computer', 'Visualiser'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Science Labs (Block B)
  {
    id: 'room-b201',
    name: 'Science Lab 1',
    code: 'B201',
    roomTypeId: 'rt-science',
    capacity: 28,
    resources: ['Lab benches', 'Fume cupboard', 'Safety equipment', 'Projector'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-b202',
    name: 'Science Lab 2',
    code: 'B202',
    roomTypeId: 'rt-science',
    capacity: 28,
    resources: ['Lab benches', 'Fume cupboard', 'Safety equipment', 'Projector'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-b203',
    name: 'Science Lab 3',
    code: 'B203',
    roomTypeId: 'rt-science',
    capacity: 28,
    resources: ['Lab benches', 'Fume cupboard', 'Safety equipment', 'Projector'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // ICT Suites (Block C)
  {
    id: 'room-c101',
    name: 'ICT Suite 1',
    code: 'C101',
    roomTypeId: 'rt-ict',
    capacity: 30,
    resources: ['30 computers', 'Projector', 'Printer'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-c102',
    name: 'ICT Suite 2',
    code: 'C102',
    roomTypeId: 'rt-ict',
    capacity: 30,
    resources: ['30 computers', 'Projector', 'Printer'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Arts Block (Block D)
  {
    id: 'room-d101',
    name: 'Art Studio 1',
    code: 'D101',
    roomTypeId: 'rt-art',
    capacity: 24,
    resources: ['Easels', 'Kiln', 'Sinks', 'Storage'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-d102',
    name: 'Art Studio 2',
    code: 'D102',
    roomTypeId: 'rt-art',
    capacity: 24,
    resources: ['Easels', 'Sinks', 'Storage'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-d201',
    name: 'Music Room',
    code: 'D201',
    roomTypeId: 'rt-music',
    capacity: 30,
    resources: ['Piano', 'Percussion', 'Recording equipment', 'Music stands'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-d202',
    name: 'Drama Studio',
    code: 'D202',
    roomTypeId: 'rt-drama',
    capacity: 30,
    resources: ['Stage area', 'Lighting', 'Sound system', 'Props storage'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Technology Block (Block E)
  {
    id: 'room-e101',
    name: 'DT Workshop 1',
    code: 'E101',
    roomTypeId: 'rt-dt',
    capacity: 20,
    resources: ['Workbenches', 'Power tools', 'CAD computers', 'Safety equipment'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-e102',
    name: 'Food Tech Kitchen',
    code: 'E102',
    roomTypeId: 'rt-food',
    capacity: 20,
    resources: ['Cooking stations', 'Ovens', 'Refrigerators', 'Prep areas'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Sports Facilities
  {
    id: 'room-sports-hall',
    name: 'Main Sports Hall',
    code: 'SPORTH',
    roomTypeId: 'rt-sports',
    capacity: 60,
    resources: ['Basketball courts', 'Badminton courts', 'Equipment storage'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'room-gym',
    name: 'Gymnasium',
    code: 'GYM',
    roomTypeId: 'rt-sports',
    capacity: 30,
    resources: ['Gymnastics equipment', 'Mats', 'Climbing wall'],
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
];
